import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  OrderSettlementMethod,
  OrderStatus,
  ServiceType
} from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import serviceApi from "@/apis/service/serviceApi";
import { Button } from "@/components/atoms/buttons/Button";
import { ButtonText } from "@/components/atoms/buttons/ButtonText";
import DateFieldBetween from "@/components/atoms/inputForms/DateFieldBetween";
import SearchField from "@/components/atoms/inputForms/SearchField";
import { Select } from "@/components/atoms/inputForms/Select";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { enumValues } from "@/libs/enums/enum";
import { orderSettlementMethodToString } from "@/libs/enums/enumStrings";
import { ReservationSearchParams } from "@/models/reservation/reservation";
import { ReservationStatusOptions } from "@/models/service/service";
import { useLoading } from "@/states/loading/operations";
import { format } from "@/utils/date";

interface Props {
  newParams: ReservationSearchParams;
  setNewParams: Dispatch<SetStateAction<ReservationSearchParams>>;
  handleClear: () => void;
  handleSearch: () => Promise<void>;
}

const ReservationSearchFieldBase: FC<Props> = ({
  newParams,
  setNewParams,
  handleClear,
  handleSearch,
}) => {
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();

  const [serviceOptions, setServiceOptions] = useState<
    { value: string; text: string, type: ServiceType }[]
  >([]);

  const [serviceMenusOptions, setServiceMenusOptions] = useState<
    { value: string; text: string }[]
  >([]);


  const isDisableMenuFilter = serviceMenusOptions?.length === 0;

  const statusOptions = useMemo(
    () =>
      ReservationStatusOptions.map((option) => ({
        value: String(option.value),
        text: option.text,
      })),
    [],
  );

  // 支払い方法のセレクトボックス
  const paymentMethodOptions: { text: string; value: string }[] = enumValues(
    OrderSettlementMethod,
  )
    .filter(
      (payment) =>
        payment === OrderSettlementMethod.CREDIT ||
        payment === OrderSettlementMethod.PAYPAL ||
        payment === OrderSettlementMethod.BANK ||
        payment === OrderSettlementMethod.LOCAL_PAYMENT,
    )
    .map((value) => ({
      value: String(value),
      text: orderSettlementMethodToString(value),
    }));

  const fetchAllService = useCallback(() => {
    void withLoading(async () => {
      try {
        const res = await serviceApi.indexServiceName();

        if (res && res.data) {
          const _serviceOptions = res.data.map((item) => ({
            value: item.id.toString(),
            text: item.name,
            type: item.type,
          }));
          setServiceOptions(_serviceOptions);
        }
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
          return;
        }
      }
    });
  }, [handlePageError, withLoading]);

  const fetchServiceMenus = useCallback((serviceId: number, serviceType: ServiceType) => {
    void withLoading(async () => {
      try {
        const response =
          serviceType === ServiceType.SUBSCRIPTION_SERVICE
            ? await serviceApi.getSubscriptionMenus({ id: serviceId })
            : await serviceApi.getServiceMenus({ id: serviceId });

        const _serviceMenusOptions = response.data.map((item) => {
          if ("service_menu_id" in item) {
            return {
              value: item.service_menu_id.toString(),
              text: item.service_menu_name,
            };
          }

          return {
            value: item.id.toString(),
            text: item.name,
          };
        });

        if (response && response.data) {
          setServiceMenusOptions(_serviceMenusOptions);
        }
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
        }
      }
    });
  }, [handlePageError, withLoading]);

  const getTypeServiceById = useCallback((id: number) => {
    const service = serviceOptions.find((service) => service.value === id.toString());
    return service?.type;
  }, [serviceOptions]);

  useEffect(() => {
    fetchAllService();
  }, [fetchAllService]);

  useEffect(() => {

    const serviceId = newParams.service_id;

    if (!serviceId) {
      setServiceMenusOptions([]);
      return;
    };

    const typeService = getTypeServiceById(serviceId);

    if (!typeService) return;

    if (typeService === (ServiceType.FORM) || typeService === (ServiceType.EVENT_SEMINAR) || typeService === (ServiceType.CALENDAR_RESERVATION) || typeService === ServiceType.SUBSCRIPTION_SERVICE)
      fetchServiceMenus(serviceId, typeService);
    else
      setServiceMenusOptions([]);

  }, [fetchServiceMenus, getTypeServiceById, newParams.service_id]);

  const handleChangeService = useCallback((id: string) => {
    setNewParams((prevState: ReservationSearchParams) => ({
      ...prevState,
      page: 1,
      service_id: Number(id),
      service_menu_id: undefined,
    }));

  }, [setNewParams]);


  return (
    <>
      <div className="flex flex-wrap gap-6 items-end py-4 mx-4 border-b md:py-0 md:mx-0 md:border-b-0">
        <div className="w-full md:w-[680px] flex-1">
          <SearchField
            value={newParams.text ?? ""}
            onChange={(e) => {
              setNewParams((prevState: ReservationSearchParams) => ({
                ...prevState,
                page: 1,
                text: e.target.value,
              }));
            }}
            placeholder="顧客氏名、メールアドレス、電話番号、注文番号"
            maxLength={500}
            className="!py-[10.5px]"
          />
        </div>
        <DateFieldBetween
          valueFrom={
            newParams.hoped_reserve_date_from
              ? new Date(newParams.hoped_reserve_date_from)
              : null
          }
          valueTo={
            newParams.hoped_reserve_date_to
              ? new Date(newParams.hoped_reserve_date_to)
              : null
          }
          label="予約日"
          className="md:w-[160px]"
          onChangeFrom={(selectedDate) => {
            setNewParams((prevState: ReservationSearchParams) => ({
              ...prevState,
              page: 1,
              hoped_reserve_date_from: selectedDate
                ? format(selectedDate, "yyyy-MM-dd")
                : undefined,
            }));
          }}
          onChangeTo={(selectedDate) => {
            setNewParams((prevState: ReservationSearchParams) => ({
              ...prevState,
              page: 1,
              hoped_reserve_date_to: selectedDate
                ? format(selectedDate, "yyyy-MM-dd")
                : undefined,
            }));
          }}
          labelClassName="mr-[16px] font-bold"
          containerClassName="!flex-row items-center"
        />
      </div>
      <div className="flex flex-wrap gap-6 items-end py-4 mx-4 border-b md:py-0 md:mx-0 md:border-b-0">
        <div className="flex flex-1 max-w-[50%] gap-4 items-center">
          <label className="block w-[90px] mb-1 font-bold">コンテンツ名</label>
          <Select
            onChange={handleChangeService}
            options={serviceOptions}
            value={
              newParams.service_id != null
                ? String(newParams.service_id)
                : undefined
            }
            placeholder="入力なし"
            className="flex-1"
          />
        </div>

        <div className="flex flex-1 gap-4 items-center">
          <label className="block w-[90px] mb-1 font-bold">申込メニュー</label>
          <Select
            onChange={(e) => {
              setNewParams((prevState: ReservationSearchParams) => ({
                ...prevState,
                page: 1,
                service_menu_id: Number(e),
              }));
            }}
            options={serviceMenusOptions}
            value={
              newParams.service_menu_id != null
                ? String(newParams.service_menu_id)
                : undefined
            }
            placeholder="入力なし"
            className="flex-1"
            isDisabled={isDisableMenuFilter}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-6 items-end p-4 md:p-0">
        <div className="flex items-center gap-4">
          <label className="block mb-1 font-bold">決済方法</label>
          <Select
            onChange={(e) => {
              setNewParams((prevState: ReservationSearchParams) => ({
                ...prevState,
                page: 1,
                payment_method: Number(e) as OrderSettlementMethod,
              }));
            }}
            options={paymentMethodOptions}
            value={
              newParams.payment_method !== undefined
                ? String(newParams.payment_method)
                : undefined
            }
            placeholder="未選択"
            className="w-[200px]"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="block mb-1 font-bold">ステータス</label>
          <Select
            onChange={(e) => {
              setNewParams((prevState: ReservationSearchParams) => ({
                ...prevState,
                page: 1,
                order_status: Number(e) as OrderStatus,
              }));
            }}
            options={statusOptions}
            value={
              newParams.order_status != null
                ? String(newParams.order_status)
                : undefined
            }
            placeholder="未選択"
            className="w-[272px]"
          />
        </div>
        <div className="hidden relative gap-x-4 justify-end p-4 ml-auto w-full md:flex md:p-0 md:w-auto">
          <div className="absolute top-0 -left-4 w-screen h-[1px] bg-theme-light md:hidden"></div>
          <ButtonText onClick={handleClear} label="すべてクリア" />
          <Button
            type="button"
            color="primary"
            size="sm"
            label="検索"
            onClick={handleSearch}
            className="!h-[44px] !min-w-[112px]"
          />
        </div>
      </div>
    </>
  );
};

export const ReservationSearchField = memo(ReservationSearchFieldBase);
